import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BrainCircuit,
  Cpu,
  Zap,
  ShieldCheck,
  Activity,
  Globe,
  GraduationCap,
  Calendar,
  Building2,
  Factory,
  Network,
  CheckCircle2,
} from "lucide-react";

const technologies = [
  {
    icon: <BrainCircuit size={28} />,
    title: "AI Analytics",
    desc: "Intelligent analytics systems delivering real-time energy insights and predictive monitoring.",
  },
  {
    icon: <Cpu size={28} />,
    title: "Industrial Automation",
    desc: "Advanced automation solutions designed for industries and smart infrastructure.",
  },
  {
    icon: <Zap size={28} />,
    title: "Energy Optimization",
    desc: "Reduce energy waste and improve operational efficiency with smart systems.",
  },
  {
    icon: <ShieldCheck size={28} />,
    title: "Reliable Monitoring",
    desc: "24/7 reliable monitoring systems with secure cloud connectivity.",
  },
  {
    icon: <Activity size={28} />,
    title: "Real-Time Dashboards",
    desc: "Interactive dashboards for live energy analytics and operational insights.",
  },
  {
    icon: <Globe size={28} />,
    title: "Sustainable Technology",
    desc: "Future-ready smart energy solutions focused on sustainability and innovation.",
  },
];

const industries = [
  {
    icon: <Factory size={26} />,
    title: "Industrial Plants",
  },
  {
    icon: <Building2 size={26} />,
    title: "Commercial Buildings",
  },
  {
    icon: <Network size={26} />,
    title: "Smart Infrastructure",
  },
];





const About = () => {

  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
  };
  return (
    <div className="relative overflow-hidden text-slate-200 bg-[#070B14]">

        {/* PREMIUM WEBSITE BACKGROUND */}
        <div
        className="absolute inset-0
        bg-[radial-gradient(circle_at_top_left,rgba(245,158,11,0.15),transparent_25%),
            radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.12),transparent_25%),
            linear-gradient(to_bottom_right,#050816,#0b1120,#111827)]"
        ></div>

        {/* EXTRA GLOW */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-yellow-500/10 rounded-full blur-[140px] animate-pulse"></div>

        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[140px] animate-pulse"></div>

        <div className="absolute top-[40%] left-[40%] w-[400px] h-[400px] bg-orange-500/5 rounded-full blur-[120px] animate-pulse"></div>

        {/* GLOW WAVES */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">

        <div
            className="absolute top-0 left-0 w-[120%] h-[120%]
            bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
            blur-3xl animate-slowWave"
        ></div>

        <div
            className="absolute bottom-0 right-0 w-[120%] h-[120%]
            bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
            blur-3xl animate-fastWave"
        ></div>

        </div>

        {/* GRID */}
        <div
        className="absolute inset-0 pointer-events-none
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
            linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
        ></div>

        {/* FLOATING PARTICLES */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">

        {[...Array(30)].map((_, i) => (
            <span
            key={i}
            className="absolute rounded-full opacity-20 animate-floatParticle"
            style={{
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`,
                background:
                i % 2 === 0
                    ? "rgba(251,191,36,0.8)"
                    : "rgba(56,189,248,0.8)",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${6 + Math.random() * 10}s`,
                animationDelay: `${Math.random() * 5}s`,
            }}
            ></span>
        ))}

        </div>

      {/* HERO */}
      <section className="relative z-10 min-h-[85vh] flex items-center justify-center px-6">

        <div className="max-w-5xl mx-auto text-center">

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >

            <div className="inline-flex items-center px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl mb-6">
              <Zap className="text-yellow-400 mr-2" size={14} />

              <span className="text-[10px] tracking-[4px] uppercase text-slate-300">
                About Power Sense
              </span>
            </div>

            <h1
              className="text-4xl md:text-6xl font-black leading-tight
              bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300
              text-transparent bg-clip-text animate-gradientMove bg-[length:200%_200%]"
            >
              Intelligent Energy
              <br />
              Monitoring Solutions
            </h1>

            <p className="max-w-3xl mx-auto mt-8 text-slate-300 text-sm md:text-base leading-relaxed">
              Power Sense is a next-generation smart energy technology company
              founded in 2024, focused on building intelligent monitoring,
              automation, AI-powered analytics, and IoT-based energy management
              systems for modern industries and smart infrastructure.
            </p>

            <p className="max-w-3xl mx-auto mt-5 text-slate-400 text-sm md:text-base leading-relaxed">
              Our mission is to help organizations improve efficiency,
              reduce operational costs, optimize energy usage, and create
              sustainable technology ecosystems through innovation and
              intelligent automation systems.
            </p>

            <div className="mt-10 flex flex-wrap justify-center gap-4">

              <button
                onClick={() => handleNavigation("/contact")}

                className="px-8 py-3 rounded-2xl bg-gradient-to-r
                from-[#f59e0b] via-[#fbbf24] to-[#f97316]
                text-black font-bold hover:scale-105 transition-all duration-500"
              >
                Contact Us
              </button>



            </div>

          </motion.div>
        </div>
      </section>

      {/* PAGE 2 */}
      <section className="relative z-10 py-20 px-6">

        <div className="max-w-7xl mx-auto">

          {/* COMPANY */}
          <div className="text-center mb-16">

            <div className="inline-flex items-center px-4 py-2 rounded-full border border-yellow-400/20 bg-yellow-400/10 mb-5">
              <span className="text-[10px] tracking-[3px] uppercase text-yellow-300">
                Company Overview
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black">
              Smart Technologies For
              <span className="bg-gradient-to-r from-yellow-300 to-cyan-300 text-transparent bg-clip-text">
                {" "}Modern Industries
              </span>
            </h2>

            <p className="max-w-4xl mx-auto mt-8 text-slate-400 text-sm md:text-base leading-relaxed">
              Power Sense develops advanced energy monitoring platforms,
              industrial automation systems, real-time analytics dashboards,
              IoT integrations, and AI-powered monitoring technologies
              that help industries improve operational visibility,
              reliability, and performance.
            </p>
          </div>

          {/* FEATURES */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">

            {technologies.map((item, index) => (
              <motion.div
                key={index}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                }}
                className="relative overflow-hidden bg-[#0f172a]/60 border border-white/10 rounded-3xl p-6 backdrop-blur-2xl before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/[0.03] before:to-transparent before:rounded-3xl before:pointer-events-none"
              >

                <div
                  className="absolute inset-0 opacity-0 hover:opacity-20
                  transition-all duration-700 blur-3xl
                  bg-gradient-to-r from-yellow-400 to-cyan-500"
                ></div>

                <div className="relative z-10">

                  <div
                    className="w-14 h-14 rounded-2xl bg-gradient-to-br
                    from-[#f59e0b] via-[#fbbf24] to-[#f97316]
                    flex items-center justify-center text-black mb-5"
                  >
                    {item.icon}
                  </div>

                  <h3 className="text-lg font-bold mb-3">
                    {item.title}
                  </h3>

                  <p className="text-slate-400 leading-relaxed text-sm">
                    {item.desc}
                  </p>

                </div>
              </motion.div>
            ))}

          </div>

          {/* INDUSTRIES */}
          <div className="mt-24">

            <div className="text-center mb-14">

              <h2 className="text-3xl md:text-5xl font-black">
                Industries
                <span className="bg-gradient-to-r from-yellow-300 to-cyan-300 text-transparent bg-clip-text">
                  {" "}We Serve
                </span>
              </h2>

            </div>

            <div className="grid md:grid-cols-3 gap-6">

              {industries.map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{
                    scale: 1.03,
                    y: -8,
                  }}
                  className="relative overflow-hidden bg-[#0f172a]/60 border border-white/10 rounded-3xl p-8 text-center backdrop-blur-2xl before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/[0.03] before:to-transparent before:rounded-3xl before:pointer-events-none"
                >

                  <div
                    className="w-16 h-16 rounded-2xl mx-auto mb-5
                    bg-gradient-to-br from-sky-500/20 via-cyan-500/20 to-blue-500/20
                    border border-cyan-400/20
                    flex items-center justify-center text-cyan-300"
                  >
                    {item.icon}
                  </div>

                  <h3 className="text-lg font-semibold">
                    {item.title}
                  </h3>

                </motion.div>
              ))}

            </div>
          </div>
        </div>
      </section>

      {/* PAGE 3 */}
      <section className="relative z-10 py-20 px-6">

        <div className="max-w-7xl mx-auto">

          {/* FOUNDER */}
          <div className="text-center mb-16">

            <div className="inline-flex items-center px-4 py-2 rounded-full border border-white/10 bg-white/5 mb-5">
              <span className="text-[10px] tracking-[3px] uppercase text-slate-300">
                Founder & Leadership
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black">
              Leadership Behind
              <span className="bg-gradient-to-r from-yellow-300 to-cyan-300 text-transparent bg-clip-text">
                {" "}Power Sense
              </span>
            </h2>
          </div>

          <div
            className="relative overflow-hidden bg-[#0f172a]/60 border border-white/10 rounded-[35px] p-8 md:p-10 backdrop-blur-2xl before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/[0.03] before:to-transparent before:rounded-[35px] before:pointer-events-none"
          >

            <div className="grid lg:grid-cols-2 gap-10">

              {/* LEFT */}
              <div>

                <div
                  className="w-20 h-20 rounded-3xl bg-gradient-to-br
                  from-[#f59e0b] via-[#fbbf24] to-[#f97316]
                  flex items-center justify-center mb-6"
                >
                  <GraduationCap size={34} className="text-black" />
                </div>

                <h3 className="text-2xl md:text-3xl font-black mb-2">
                  Pradeep Kumar Yemula
                </h3>

                <p className="text-yellow-300 font-semibold mb-5">
                  Founder & Associate Professor
                </p>

                <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                  Power Sense was founded in 2024 by Pradeep Kumar Yemula
                  with the vision of creating intelligent energy systems
                  that combine AI, IoT, automation, and analytics to
                  transform industrial infrastructure and smart energy
                  management.
                </p>

              </div>

              {/* RIGHT */}
              <div className="space-y-5">

                <div className="bg-white/5 border border-white/10 rounded-3xl p-5">

                  <div className="flex items-center gap-3 mb-3">

                    <GraduationCap
                      className="text-yellow-300"
                      size={20}
                    />

                    <h4 className="font-bold">
                      Education
                    </h4>

                  </div>

                  <p className="text-slate-400 text-sm">
                    Ph.D — IIT Bombay
                  </p>

                </div>

                <div className="bg-white/5 border border-white/10 rounded-3xl p-5">

                  <div className="flex items-center gap-3 mb-3">

                    <Calendar
                      className="text-cyan-300"
                      size={20}
                    />

                    <h4 className="font-bold">
                      Founded
                    </h4>

                  </div>

                  <p className="text-slate-400 text-sm">
                    Established in 2024
                  </p>

                </div>

                <div className="bg-white/5 border border-white/10 rounded-3xl p-5">

                  <h4 className="font-bold mb-4">
                    Research Interests
                  </h4>

                  <div className="flex flex-wrap gap-3">

                    {[
                      "Smart Grids",
                      "Power Systems",
                      "IoT",
                      "Energy",
                      "CIM",
                      "Automation",
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 rounded-full
                        bg-yellow-400/10 border border-yellow-400/20
                        text-yellow-200 text-xs"
                      >
                        {item}
                      </div>
                    ))}

                  </div>

                </div>

              </div>

            </div>
          </div>

          {/* CTA */}
          <div
            className="relative mt-20 bg-gradient-to-r
            from-[#f59e0b] via-[#fbbf24] to-[#f97316]
            rounded-[4rem] overflow-hidden"
          >

            <div className="relative z-10 px-8 md:px-20 py-20 text-center">

              <h2 className="text-3xl md:text-5xl font-black text-black leading-tight">
                Ready To Build
                <br />
                Smarter Energy Systems?
              </h2>

              <p className="max-w-3xl mx-auto mt-6 text-black/80 text-sm md:text-base leading-relaxed">
                Partner with Power Sense to transform your infrastructure
                through intelligent monitoring, industrial automation,
                AI-powered analytics, and future-ready smart energy
                technologies.
              </p>

              <button
                onClick={() => handleNavigation("/contact")}
                className="mt-8 bg-black text-yellow-400 px-8 py-3 rounded-2xl
                font-bold hover:scale-105 transition-all duration-500"
              >
                Contact Us
              </button>

            </div>
          </div>

        </div>
      </section>

{/* ANIMATIONS */}
<style>{`

@keyframes slowWave {
  0% {
    transform: translate(-10%, -10%) rotate(0deg);
  }
  50% {
    transform: translate(10%, 10%) rotate(14deg);
  }
  100% {
    transform: translate(-10%, -10%) rotate(0deg);
  }
}

@keyframes fastWave {
  0% {
    transform: translate(10%, 10%) rotate(0deg);
  }
  50% {
    transform: translate(-10%, -10%) rotate(-14deg);
  }
  100% {
    transform: translate(10%, 10%) rotate(0deg);
  }
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes floatParticle {
  0% {
    transform: translateY(0px) scale(1);
    opacity: 0.2;
  }

  50% {
    transform: translateY(-40px) scale(1.4);
    opacity: 0.8;
  }

  100% {
    transform: translateY(-80px) scale(1);
    opacity: 0;
  }
}

.animate-gradientMove {
  animation: gradientMove 6s ease infinite;
}

.animate-slowWave {
  animation: slowWave 14s ease-in-out infinite;
}

.animate-fastWave {
  animation: fastWave 10s ease-in-out infinite;
}

.animate-floatParticle {
  animation: floatParticle linear infinite;
}

`}</style>

    </div>
  );
};

export default About;